import { useState } from 'react'
import { PlusLg } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel, FormModal, RowActions, Stat } from '../components/ui.jsx'
import { entries, loanBalance, annualToDaily, dailyToAnnual } from '../lib/finance.js'
import { money, dateLabel, todayISO, number, toISO } from '../lib/format.js'
import { add, setAt, removeAt } from '../lib/firebase.js'

const FIELDS = [
  { name: 'name', label: 'Name', required: true },
  { name: 'balance', label: 'Confirmed balance', type: 'number', required: true, step: '0.01', col: 'col-6', help: 'From your bank statement.' },
  { name: 'asOf', label: 'As of', type: 'date', required: true, col: 'col-6' },
  { name: 'annualRate', label: 'Annual interest %', type: 'number', step: '0.001', col: 'col-6', help: 'Compounded daily.' },
  { name: 'monthlyPayment', label: 'Monthly payment', type: 'number', step: '0.01', col: 'col-6' },
  { name: 'paymentDay', label: 'Payment day', type: 'number', step: '1', col: 'col-6' },
]

/** Months until the estimated balance reaches zero, or null if payments don't cover interest. */
function payoff(loan) {
  if (!loan.monthlyPayment) return null
  const start = todayISO()
  let bal = loanBalance(loan, start)
  const d = new Date()
  for (let i = 0; i < 365 * 40 && bal > 0; i++) {
    d.setDate(d.getDate() + 1)
    bal *= 1 + (loan.dailyRate || 0)
    if (d.getDate() === Number(loan.paymentDay)) bal -= loan.monthlyPayment
    if (bal <= 0) return toISO(d)
  }
  return null
}

export default function Loans() {
  const data = useData()
  const [edit, setEdit] = useState(null)
  const today = todayISO()
  const loans = entries(data.loans)
  const total = loans.reduce((s, l) => s + loanBalance(l, today), 0)

  async function save(values) {
    const { id, ...rest } = edit
    const { annualRate, ...v } = values
    const rec = { ...rest, ...v, dailyRate: annualRate ? annualToDaily(annualRate) : 0 }
    delete rec.annualRate
    if (id) await setAt(`loans/${id}`, rec)
    else await add('loans', rec)
  }

  return (
    <>
      <PageHeader title="Loans" subtitle="Balances are estimated daily from the last confirmed balance, interest and payments.">
        <button className="btn btn-primary" onClick={() => setEdit({ asOf: today, paymentDay: 1 })}><PlusLg /> Loan</button>
      </PageHeader>
      <div className="row g-3 mb-3">
        <div className="col-6 col-lg-3"><Stat label="Total debt today" value={money(total)} /></div>
        <div className="col-6 col-lg-3"><Stat label="Monthly payments" value={money(loans.reduce((s, l) => s + (l.monthlyPayment || 0), 0))} /></div>
      </div>
      <Panel>
        {loans.length ? (
          <div className="table-wrap"><table className="table align-middle">
            <thead><tr><th>Name</th><th className="text-end">Confirmed</th><th className="text-end">Estimated today</th><th className="text-end">Interest</th><th className="text-end">Payment</th><th>Paid off</th><th /></tr></thead>
            <tbody>
              {loans.map((l) => {
                const est = loanBalance(l, today)
                const p = payoff(l)
                return (
                  <tr key={l.id}>
                    <td>{l.name}</td>
                    <td className="num">{money(l.balance)}<div className="small text-body-secondary">{dateLabel(l.asOf)}</div></td>
                    <td className="num fw-semibold">{money(est)}<div className="small text-body-secondary">{money(est - l.balance, { sign: true })}</div></td>
                    <td className="num">{number(dailyToAnnual(l.dailyRate || 0), 2)}%<div className="small text-body-secondary">≈ {money(est * (l.dailyRate || 0) * 30.4)}/mo</div></td>
                    <td className="num">{l.monthlyPayment ? money(l.monthlyPayment) : '–'}<div className="small text-body-secondary">{l.paymentDay ? `on day ${l.paymentDay}` : ''}</div></td>
                    <td className="small">{p ? dateLabel(p) : l.monthlyPayment ? <span className="neg">payment doesn't cover interest</span> : '–'}</td>
                    <td className="text-end"><RowActions onEdit={() => setEdit({ ...l, annualRate: Number(dailyToAnnual(l.dailyRate || 0).toFixed(3)) })} onDelete={() => removeAt(`loans/${l.id}`)} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table></div>
        ) : <div className="empty">No loans. 🎉</div>}
      </Panel>
      {edit && <FormModal title={edit.id ? 'Edit loan' : 'New loan'} fields={FIELDS} initial={edit} onSubmit={save} onClose={() => setEdit(null)} />}
    </>
  )
}
